// The restaurant rollups — customers (by customer, frequency, recency or
// suburb), loyalty (accrual vs redemption, with the liability summary) and
// coupons — across every store or the ones picked (storeIds=). One report
// shape, rendered by the shared typed table; CSV/XLSX through format=.

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { api, type Report } from '../../lib/api';
import { downloadReport, ExportButtons, PeriodPicker, periodParams, ReportTable, type Period } from './report';
import type { Store } from './types';

type Tab = { key: 'customers' | 'loyalty' | 'coupons'; title: string; hide?: string[] };
const TABS: Tab[] = [
  { key: 'customers', title: 'Customers' },
  { key: 'loyalty', title: 'Loyalty' },
  { key: 'coupons', title: 'Coupons', hide: ['coupon_id'] },
];

const GROUPS = [
  { key: 'customer', label: 'By customer' },
  { key: 'frequency', label: 'By frequency' },
  { key: 'recency', label: 'By recency' },
  { key: 'suburb', label: 'By suburb' },
];

export function BrandReports() {
  const { rid = '' } = useParams();
  const [tab, setTab] = useState<Tab>(TABS[0]);
  const [groupBy, setGroupBy] = useState('customer');
  const [period, setPeriod] = useState<Period>({ preset: 'today', from: '', to: '' });
  const [stores, setStores] = useState<Store[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    api.get<Store[]>(`/restaurants/${rid}/stores`).then(setStores).catch(() => setStores([]));
  }, [rid]);

  const path = `/restaurants/${rid}/reports/${tab.key}`;
  const params: Record<string, string> = {
    ...periodParams(period),
    ...(tab.key === 'customers' ? { groupBy } : {}),
    ...(picked.length > 0 ? { storeIds: picked.join(',') } : {}),
  };
  const key = JSON.stringify([path, params]);
  const ready = period.preset !== null || (period.from !== '' && period.to !== '');

  useEffect(() => {
    if (!ready) return;
    const mine = ++seq.current;
    setReport(null);
    api.get<Report>(path, params)
      .then((r) => { if (mine === seq.current) { setReport(r); setError(null); } })
      .catch((e: unknown) => { if (mine === seq.current) setError(e instanceof Error ? e.message : 'Failed to load.'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);

  async function exportAs(format: 'csv' | 'xlsx') {
    try {
      const r = report;
      const stem = `brand-${tab.key}${r ? `-${r.range.from}-${r.range.to}` : ''}`;
      await downloadReport(path, params, format, stem);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    }
  }

  const toggle = (sid: string) => setPicked((p) => (p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]));

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Restaurant · Reports</div>
          <h1>Brand reports</h1>
        </div>
        <span style={{ marginLeft: 'auto' }}>
          <ExportButtons onExport={(f) => void exportAs(f)} disabled={!report} />
        </span>
      </div>

      <div className="filters" style={{ marginBottom: 12 }} data-section="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={'filter' + (tab.key === t.key ? ' on' : '')} onClick={() => setTab(t)}>
            {t.title}
          </button>
        ))}
      </div>
      <div style={{ marginBottom: 12 }}>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      {tab.key === 'customers' && (
        <div className="filters" style={{ marginBottom: 12 }} data-section="group-by">
          {GROUPS.map((g) => (
            <button key={g.key} className={'filter' + (groupBy === g.key ? ' on' : '')} onClick={() => setGroupBy(g.key)}>
              {g.label}
            </button>
          ))}
        </div>
      )}
      {stores.length > 0 && (
        <div className="filters" style={{ marginBottom: 16 }} data-section="store-filter">
          <button className={'filter' + (picked.length === 0 ? ' on' : '')} onClick={() => setPicked([])}>All stores</button>
          {stores.map((s) => (
            <button key={s.storeId} className={'filter' + (picked.includes(s.storeId) ? ' on' : '')}
              aria-pressed={picked.includes(s.storeId)} onClick={() => toggle(s.storeId)}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {error && <div className="err" role="alert">{error}</div>}
      {!ready && <div className="loading">Pick both dates.</div>}
      {ready && report === null && !error && <div className="loading">Loading…</div>}
      {report && (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 10px' }}>
            {report.range.from === report.range.to ? report.range.from : `${report.range.from} → ${report.range.to}`} · {report.timezone}
          </p>
          {/* by customer, the key is the customer id — the name says it */}
          <ReportTable report={report} hide={tab.key === 'customers' && groupBy === 'customer' ? ['key'] : tab.hide} />
        </>
      )}
    </>
  );
}
